import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ParticipantNotes } from "./participantNotes";
import { DateRangeNotes } from "./dateRangeNotes";
import { MostRecentEvents } from "./mostRecentEvents";
import { DateRangeReport } from "./dateRangeReport";
import { PUPM } from "./pupm";
import { FeeForServiceReport } from "./feeForService";
import { PMPM } from "./pmpm";
import { GenerateBillingReport } from "../../CalAIM/billingManager/generateBillingReport";
import { BillingSftpFiles } from '../../billing/sftp/BillingSftpFiles';
import { BillingSftpAdmin } from '../../billing/sftp/BillingSftpAdmin';
import { BillingEcmUsers } from './users/BillingEcmUsers';
import { cn } from '@/lib/utils';
import { useUser } from '@/store/useUser';
import { useUserPermissions } from '@/store/useUserPermissions';
import { normalizePolicyList, policyMatches } from '@/utils/policies';
import {
  Settings,
  FileText,
  Users,
  BarChart,
  PieChart,
  DollarSign,
  Calendar,
  Activity,
  Search,
  Star,
  StarOff
} from 'lucide-react';

interface BillingPanel {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  policies: string[];
  keywords: string[];
  category: string;
}

const billingPanels: BillingPanel[] = [
  {
    id: 'ecm-users', title: 'ECM User Manager', description: 'Create limited LCM accounts and manage passwords and authenticators',
    icon: Users, path: '/billing/ecm-users', policies: ['tools:billing:admin~full'],
    keywords: ['ecm', 'lcm', 'users', 'accounts', 'password', 'totp'], category: 'Administration'
  },
  {
    id: 'sftp-files', title: 'LA Care Files', description: 'Incoming and outgoing files, processing and archive state',
    icon: FileText, path: '/billing/sftp', policies: ['tools:billing:access~full'],
    keywords: ['sftp', 'la care', 'files', 'upload', 'download', 'mif'], category: 'Communications'
  },
  {
    id: 'sftp-admin', title: 'SFTP Administration', description: 'Connection settings, file types and folders',
    icon: Settings, path: '/billing/sftp/admin', policies: ['billing:sftp~admin'],
    keywords: ['sftp', 'settings', 'folders', 'types', 'la care'], category: 'Administration'
  },
  {
    id: 'participant-billing',
    title: 'Participant Billing',
    description: 'View billing notes for a specific participant',
    icon: Users,
    path: '/billing/participant',
    policies: ['tools:billing:access~full'],
    keywords: ['participant', 'billing', 'notes', 'client'],
    category: 'Participant'
  },
  {
    id: 'date-range-notes',
    title: 'Date Range Notes',
    description: 'View billing notes within a date range',
    icon: Calendar,
    path: '/billing/date-range-notes',
    policies: ['tools:billing:access~full'],
    keywords: ['date', 'range', 'notes', 'calendar'],
    category: 'Reports'
  },
  {
    id: 'most-recent-events',
    title: 'Most Recent Events',
    description: 'Most recent coordinated care note per participant',
    icon: Activity,
    path: '/billing/most-recent-events',
    policies: ['tools:billing:access~full'],
    keywords: ['recent', 'notes', 'coordinated', 'care'],
    category: 'Reports'
  },
  {
    id: 'billing-report',
    title: 'Billing Report',
    description: 'Generate general billing reports',
    icon: FileText,
    path: '/billing/report',
    policies: ['tools:billing:access~full'],
    keywords: ['billing', 'report', 'general'],
    category: 'Reports'
  },
  {
    id: 'calaim-billing',
    title: 'CalAIM Billing',
    description: 'Generate CalAIM billing reports and CSVs',
    icon: Activity,
    path: '/billing/calaim',
    policies: ['tools:billing:access~full'],
    keywords: ['calaim', 'billing', 'csv', 'events'],
    category: 'CalAIM'
  },
  {
    id: 'pupm-report',
    title: 'PUPM Report',
    description: 'Per Unit Per Month reports',
    icon: BarChart,
    path: '/billing/pupm',
    policies: ['tools:billing:access~full'],
    keywords: ['pupm', 'unit', 'month', 'report'],
    category: 'Metrics'
  },
  {
    id: 'fee-for-service',
    title: 'Fee For Service',
    description: 'Fee For Service billing reports',
    icon: DollarSign,
    path: '/billing/fee-for-service',
    policies: ['tools:billing:access~full'],
    keywords: ['fee', 'service', 'ffs', 'billing'],
    category: 'Metrics'
  },
  {
    id: 'pmpm-report',
    title: 'PMPM Report',
    description: 'Per Member Per Month reports',
    icon: PieChart,
    path: '/billing/pmpm',
    policies: ['tools:billing:access~full'],
    keywords: ['pmpm', 'member', 'month', 'report'],
    category: 'Metrics'
  },
  {
    id: 'ecm-dashboard',
    title: 'ECM Dashboard',
    description: 'Open ECM dashboard census and operations views',
    icon: Activity,
    path: '/tools/ecm-dashboard',
    policies: ['tools:billing:access~full'],
    keywords: ['ecm', 'dashboard', 'census', 'operations'],
    category: 'ECM'
  }
];

export const BillingCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [localFavorites, setLocalFavorites] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { participant } = useParams();
  
  // Note: We're reusing admin settings favorites for now, or we could add a new setting field
  const { user, updateAdminFavorites } = useUser();
  const { policies } = useUserPermissions();
  const policyList = useMemo(() => normalizePolicyList(policies), [policies]);
  const canAccessPanel = (panel: BillingPanel) => {
    if (policyList.includes('*:*~*')) {
      return true;
    }
    return panel.policies.some((requiredPolicy) =>
      policyList.some((userPolicy) => policyMatches(userPolicy, requiredPolicy))
    );
  };

  // Sync local favorites with user settings
  useEffect(() => {
    if (user?.settings?.adminSettingsFavorites) {
      setLocalFavorites(user.settings.adminSettingsFavorites);
    }
  }, [user]);

  // Toggle favorite status
  const toggleFavorite = async (panelId: string) => {
    const newFavorites = localFavorites.includes(panelId)
      ? localFavorites.filter(id => id !== panelId)
      : [...localFavorites, panelId];
    
    // Update local state immediately
    setLocalFavorites(newFavorites);
    
    // Update backend
    const result = await updateAdminFavorites(newFavorites);
    if (!result.success) {
      console.error('Failed to update favorites:', result.error);
    }
  };

  const filteredPanels = useMemo(() => {
    let panels = billingPanels.filter(canAccessPanel);

    if (selectedCategory === 'Favorites') {
      panels = panels.filter(panel => localFavorites.includes(panel.id));
    }
    
    if (selectedCategory && selectedCategory !== 'Favorites') {
      panels = panels.filter(panel => panel.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      panels = panels.filter(panel => 
        panel.title.toLowerCase().includes(query) ||
        panel.description.toLowerCase().includes(query) ||
        panel.keywords.some(keyword => keyword.toLowerCase().includes(query))
      );
    }

    return panels;
  }, [searchQuery, selectedCategory, localFavorites, policyList]);

  const groupedPanels = useMemo(() => {
    const groups: { [key: string]: BillingPanel[] } = {};
    
    if (selectedCategory === 'Favorites') {
      groups['Favorites'] = filteredPanels;
    }
    
    if (!selectedCategory && localFavorites.length > 0) {
      const favoritePanels = billingPanels.filter(panel => localFavorites.includes(panel.id) && canAccessPanel(panel));
      if (favoritePanels.length > 0) {
        groups['Favorites'] = favoritePanels;
      }
    }
    
    filteredPanels.forEach(panel => {
      if (selectedCategory === 'Favorites') return;
      if (!selectedCategory && localFavorites.includes(panel.id)) return;
      
      if (!groups[panel.category]) {
        groups[panel.category] = [];
      }
      groups[panel.category].push(panel);
    });

    return groups;
  }, [filteredPanels, selectedCategory, localFavorites, policyList]);

  const handlePanelClick = (panel: BillingPanel) => {
    navigate(panel.path);
  };

  const isActivePanel = (panel: BillingPanel) => {
    // Handle exact match or sub-paths (like participant with ID)
    return location.pathname === panel.path || location.pathname.startsWith(`${panel.path}/`);
  };

  // Wrapper for ParticipantNotes to handle the ID param
  const ParticipantNotesWrapper = () => {
    return <ParticipantNotes participant={participant} />;
  };

  const renderContent = () => {
    const path = location.pathname;
    if (path === '/billing/sftp') return <BillingSftpFiles />;
    if (path === '/billing/sftp/admin') return <BillingSftpAdmin />;
    if (path === '/billing/ecm-users') return <BillingEcmUsers />;

    if (path.startsWith('/billing/participant')) return <ParticipantNotesWrapper />;
    if (path === '/billing/date-range-notes') return <DateRangeNotes />;
    if (path === '/billing/most-recent-events') return <MostRecentEvents />;
    if (path === '/billing/report') return <DateRangeReport />;
    if (path === '/billing/pupm') return <PUPM />;
    if (path === '/billing/fee-for-service') return <FeeForServiceReport />;
    if (path === '/billing/pmpm') return <PMPM />;
    if (path === '/billing/calaim') return <GenerateBillingReport />;
    
    // Default welcome screen
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <Settings className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Select a billing panel from the sidebar</p>
          <p className="text-sm mt-2">Use search to quickly find specific reports</p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen gap-4 bg-gray-50 px-0 pb-4 sm:px-1 lg:px-1 xl:px-2">
      <div className="app-page-shell flex w-80 flex-col overflow-hidden">
        <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)]">
          <div className="px-3 py-3">
            <h1 className="mb-3 text-xl font-semibold text-[var(--neutral-gray-120)]">Billing Center</h1>
            
            <div className="relative w-full">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="box-border h-8 w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoComplete="off"
                name="billing-search"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-3 pt-4">
          {Object.entries(groupedPanels).map(([category, panels]) => (
            <div key={category} className="mb-4">
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {category}
              </h3>
              <div className="space-y-1">
                {panels.map(panel => {
                  const Icon = panel.icon;
                  const isFavorite = localFavorites.includes(panel.id);
                  return (
                    <div key={panel.id} className="relative group/panel">
                      <button
                        id={panel.id === 'ecm-users' ? 'billing-ecm-users-panel' : undefined}
                        onClick={() => handlePanelClick(panel)}
                        className={cn(
                          "group flex w-full items-center rounded-lg border px-2.5 py-2 transition-all duration-150",
                          isActivePanel(panel)
                            ? "border-[var(--azure-primary-border-soft)] bg-[var(--neutral-gray-10)] text-[var(--azure-primary)]"
                            : "border-transparent text-gray-700 hover:bg-[var(--neutral-gray-10)] hover:text-gray-900"
                        )}
                      >
                        <Icon className={cn(
                          "mr-2.5 h-4 w-4 flex-shrink-0",
                          isActivePanel(panel) ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"
                        )} />
                        <div className="text-left flex-1">
                          <div className="text-xs font-medium leading-4">{panel.title}</div>
                          <div className="mt-0.5 line-clamp-1 text-xs leading-4 text-gray-500">
                            {panel.description}
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(panel.id);
                        }}
                        className={cn(
                          "absolute right-1.5 top-1.5 rounded p-1 transition-all duration-150 opacity-0 group-hover/panel:opacity-100",
                          isFavorite 
                            ? "text-yellow-500 hover:text-yellow-600 opacity-100" 
                            : "text-gray-400 hover:text-yellow-500"
                        )}
                        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                      >
                        {isFavorite ? (
                          <Star className="w-4 h-4 fill-current" />
                        ) : (
                          <StarOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="app-page-shell flex-1 overflow-hidden overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
};
